<script setup lang="ts">
import {computed, onMounted, ref, watch} from 'vue';
import Chart from 'primevue/chart';
import metricsBackend, {type PulseAllocationDataPoint} from '@/backend/metrics.backend';
import {DateTime} from 'luxon';
import Skeleton from 'primevue/skeleton';
import Button from 'primevue/button';
import {useTimeAllocationColors} from '@/composables/useTimeAllocationColors';
import {useMetricsAllocationStats} from '@/composables/usePulseStats';

interface Props {
  from: Date;
  to: Date;
  department?: string;
  teamId?: number;
  userId?: number;
}

const props = defineProps<Props>();

const loading = ref(false);
const error = ref<string | null>(null);
const allocationData = ref<PulseAllocationDataPoint[]>([]);

// Get colors for categories
const { getCategoryColor } = useTimeAllocationColors();

// Use centralized allocation stats calculation
const { stats: aggregatedStats } = useMetricsAllocationStats(allocationData);

// Chart data computed property
const chartData = computed(() => {
  const { companyAllocation, hasData } = aggregatedStats.value;
  
  if (!hasData) {
    return null;
  }

  // Filter out zero values and prepare data
  const dataPoints = Object.entries(companyAllocation)
    .filter(([_, value]) => (value as number) > 0)
    .map(([key, value]) => ({
      key,
      label: formatCategoryLabel(key),
      value: Math.round(value as number),
      color: key === 'unallocated' ? '#F3F4F6' : getCategoryColor(key)
    }))
    .sort((a, b) => {
      // Sort by value descending, but keep unallocated at the end
      if (a.key === 'unallocated') return 1;
      if (b.key === 'unallocated') return -1;
      return b.value - a.value;
    });

  return {
    labels: dataPoints.map(point => point.label),
    datasets: [{
      data: dataPoints.map(point => point.value),
      backgroundColor: dataPoints.map(point => point.color),
      borderColor: dataPoints.map(point => point.color),
      borderWidth: 1
    }]
  };
});

// Format category labels for display
function formatCategoryLabel(categoryKey: string): string {
  if (categoryKey === 'unallocated') return 'No data';
  
  // Convert snake_case or kebab-case to Title Case
  return categoryKey
    .split(/[-_]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Chart options
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  devicePixelRatio: 1,
  plugins: {
    legend: {
      position: 'right' as const,
      align: 'center' as const,
      labels: {
        usePointStyle: true,
        padding: 12,
        boxWidth: 8,
        font: {
          size: 10
        }
      }
    },
    tooltip: {
      callbacks: {
        label: (context: any) => {
          const label = context.label || '';
          const value = context.parsed || 0;
          return `${label}: ${value}%`;
        }
      }
    }
  },
  layout: {
    padding: {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0
    }
  },
  elements: {
    arc: {
      borderWidth: 1
    }
  }
}));

// Total stats for accessibility
const totalStats = computed(() => {
  if (!chartData.value) return null;
  
  const total = chartData.value.datasets[0].data.reduce((sum: number, val: number) => sum + val, 0);
  const categories = chartData.value.labels.length;
  
  return {
    total,
    categories,
    range: `${DateTime.fromJSDate(props.from).toFormat('MMM dd, yyyy')} - ${DateTime.fromJSDate(props.to).toFormat('MMM dd, yyyy')}`
  };
});

// Fetch data function
async function fetchData() {
  if (loading.value) return;
  
  loading.value = true;
  error.value = null;
  
  try {
    const fromStr = DateTime.fromJSDate(props.from).toISODate();
    const toStr = DateTime.fromJSDate(props.to).toISODate();
    
    if (!fromStr || !toStr) {
      throw new Error('Invalid date range');
    }
    
    // Get the user's timezone
    const timezone = DateTime.local().zoneName;
    
    const response = await metricsBackend.getPulseAllocationData(fromStr, toStr, props.department, timezone, props.teamId, props.userId);
    allocationData.value = response.data || [];
    
  } catch (err) {
    console.error('Failed to fetch pulse allocation data:', err);
    error.value = err instanceof Error ? err.message : 'Failed to load data';
    allocationData.value = [];
  } finally {
    loading.value = false;
  }
}

// Retry function
function retry() {
  fetchData();
}

// Watch for prop changes
watch(() => [props.from, props.to, props.department, props.teamId, props.userId], fetchData, { immediate: true });

// Initial fetch
onMounted(() => {
  if (!allocationData.value.length) {
    fetchData();
  }
});
</script>

<template>
  <div class="metrics-allocation-chart">
    <!-- Loading State -->
    <div v-if="loading" class="space-y-4">
      <Skeleton height="200px" class="mb-4" />
      <div class="flex justify-center gap-4">
        <Skeleton width="80px" height="20px" />
        <Skeleton width="120px" height="20px" />
        <Skeleton width="100px" height="20px" />
      </div>
    </div>
    
    <!-- Error State -->
    <div v-else-if="error" class="text-center py-8">
      <div class="text-red-600 mb-4">
        <i class="pi pi-exclamation-triangle text-2xl mb-2"></i>
        <p class="text-sm">{{ error }}</p>
      </div>
      <Button 
        label="Retry" 
        icon="pi pi-refresh" 
        @click="retry"
        severity="secondary"
      />
    </div>
    
    <!-- No Data State -->
    <div v-else-if="!aggregatedStats.hasData" class="text-center py-16">
      <div class="text-muted-color mb-4">
        <i class="pi pi-chart-pie text-4xl mb-4 block"></i>
        <p class="text-lg font-medium mb-2">No Work Allocation Data</p>
        <p class="text-sm">No work allocation data available for this period.</p>
      </div>
    </div>
    
    <!-- Chart -->
    <div v-else class="chart-wrapper">
      <!-- Pie Chart -->
      <div class="chart-container">
        <Chart
          type="pie"
          class="pie-chart"
          :data="chartData"
          :options="chartOptions"
          :key="`chart-${chartData?.labels?.length || 0}`"
          :canvasProps="{
            'role': 'img',
            'aria-label': `Work allocation pie chart for the period from ${DateTime.fromJSDate(props.from).toFormat('MMM dd, yyyy')} to ${DateTime.fromJSDate(props.to).toFormat('MMM dd, yyyy')}`
          }"
        />
      </div>
      
      <!-- Accessibility Summary -->
      <div v-if="totalStats" class="sr-only">
        <p>Work allocation breakdown for {{ totalStats.range }}:</p>
        <ul>
          <li v-for="(label, index) in chartData!.labels" :key="label">
            {{ label }}: {{ chartData!.datasets[0].data[index] }}%
          </li>
        </ul>
        <p>
          Total of {{ totalStats.categories }} categories representing {{ totalStats.total }}% of allocated time.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.metrics-allocation-chart {
  width: 100%;
}

.chart-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 0;
}

.chart-container {
  width: 100%;
  max-width: 400px;
  height: 280px;
  position: relative;
}

.pie-chart {
  width: 100% !important;
  height: 100% !important;
}

.stat-card {
  padding: 0.75rem;
  background-color: rgb(248 250 252);
  border-radius: 0.5rem;
  border: 1px solid rgb(226 232 240);
}

:global(.dark) .stat-card {
  background-color: rgb(15 23 42);
  border-color: rgb(51 65 85);
}

/* Responsive adjustments */
@media (max-width: 1280px) {
  .chart-container {
    max-width: 100%;
    height: 300px;
  }
}

@media (max-width: 768px) {
  .chart-container {
    height: 280px;
  }
}
</style>